import React from 'react'

import { ConfirmDialog } from '@mangovoice/mango-ui'

import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'

export interface SwitchboardDeleteDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean
  /** Callback when dialog should close */
  onClose: () => void
  /** Display name of the switchboard being deleted */
  displayName: string
  /** Callback when delete is confirmed */
  onConfirm: () => void
  /** Whether delete operation is in progress */
  isDeleting: boolean
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
}

/**
 * Confirmation dialog for deleting a switchboard
 */
export const SwitchboardDeleteDialog = React.memo<SwitchboardDeleteDialogProps>(
  ({ isOpen, onClose, displayName, onConfirm, isDeleting, t }) => {
    const unnamedSwitchboard = text(
      t,
      'switchboard.list.common.unnamedSwitchboard',
      'Unnamed Switchboard',
    )
    const bodyText = text(
      t,
      'switchboard.list.delete.body',
      'Are you sure you want to delete "{{displayName}}"? This action cannot be undone.',
      { displayName: displayName || unnamedSwitchboard },
    )
    return (
      <ConfirmDialog
        open={isOpen}
        onClose={onClose}
        title={text(t, 'switchboard.list.delete.title', 'Delete Switchboard')}
        description={bodyText}
        confirmLabel={text(t, 'switchboard.list.delete.confirm', 'Delete')}
        cancelLabel={text(t, 'switchboard.list.delete.cancel', 'Cancel')}
        onConfirm={onConfirm}
        variant="destructive"
        isLoading={isDeleting}
      />
    )
  },
)

SwitchboardDeleteDialog.displayName = 'SwitchboardDeleteDialog'
